import React, { useEffect, useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const WINDOW = 120;
const LABELS = Array.from({ length: WINDOW }, (_, i) => i);
const FPS = 30;
const DRAIN_HZ = 30;

interface BPWaveChartProps {
  isMonitoring: boolean;
  bpSignal?: number[];
  bufferFrames?: number;
  // Verification-demo overlay only (see RecordedDemoPage.tsx / IPPGChart.tsx
  // for the full explanation). Undefined on every live-monitoring page.
  groundTruthSignal?: number[];
}

// See IPPGChart.tsx for why this queue+fixed-rate-drain exists, and for why
// bufferFrames (an exact server-side counter) drives the new-sample count
// instead of a wall-clock estimate.
const BPWaveChart: React.FC<BPWaveChartProps> = ({ isMonitoring, bpSignal, bufferFrames, groundTruthSignal }) => {
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const queueRef = useRef<number[]>([]);
  const gtQueueRef = useRef<number[]>([]);
  const lastUpdateAtRef = useRef<number>(0);
  const lastBufferFramesRef = useRef<number>(0);
  const hasGroundTruth = groundTruthSignal !== undefined;

  const chartData = useMemo(() => ({
    labels: LABELS,
    datasets: [
      {
        label: 'BP',
        data: new Array(WINDOW).fill(0) as number[],
        borderColor: '#60a5fa',
        backgroundColor: 'rgba(96,165,250,0.08)',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1.5,
      },
      ...(hasGroundTruth ? [{
        label: 'Ground truth',
        data: new Array(WINDOW).fill(0) as number[],
        borderColor: 'rgba(255,255,255,0.45)',
        backgroundColor: 'transparent',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [4, 3],
      }] : []),
    ],
  }), [hasGroundTruth]);

  useEffect(() => {
    if (!isMonitoring) return;
    if (!bpSignal || bpSignal.length === 0) return;
    const flat = bpSignal.flat().filter((v) => typeof v === 'number' && !isNaN(v));
    if (flat.length === 0) return;
    let newCount: number;
    if (typeof bufferFrames === 'number' && bufferFrames > 0) {
      const diff = bufferFrames - lastBufferFramesRef.current;
      newCount = diff > 0 ? Math.min(flat.length, diff) : flat.length;
      lastBufferFramesRef.current = bufferFrames;
    } else {
      const now = performance.now();
      const dtMs = lastUpdateAtRef.current ? now - lastUpdateAtRef.current : Infinity;
      newCount = Math.min(flat.length, Math.max(1, Math.round((dtMs / 1000) * FPS)));
    }
    lastUpdateAtRef.current = performance.now();
    queueRef.current.push(...flat.slice(-newCount));
    if (queueRef.current.length > 600) {
      queueRef.current = queueRef.current.slice(-600);
    }
    if (groundTruthSignal && groundTruthSignal.length > 0) {
      const gtFlat = groundTruthSignal.flat().filter((v) => typeof v === 'number' && !isNaN(v));
      if (gtFlat.length > 0) {
        gtQueueRef.current.push(...gtFlat.slice(-Math.min(gtFlat.length, newCount)));
        if (gtQueueRef.current.length > 600) {
          gtQueueRef.current = gtQueueRef.current.slice(-600);
        }
      }
    }
  }, [bpSignal, groundTruthSignal, isMonitoring, bufferFrames]);

  useEffect(() => {
    if (!isMonitoring) {
      if (chartRef.current) {
        (chartRef.current.data.datasets[0].data as number[]).fill(0);
        if (hasGroundTruth && chartRef.current.data.datasets[1]) {
          (chartRef.current.data.datasets[1].data as number[]).fill(0);
        }
        chartRef.current.update('none');
      }
      queueRef.current = [];
      gtQueueRef.current = [];
      lastUpdateAtRef.current = 0;
      lastBufferFramesRef.current = 0;
      return;
    }
    // requestAnimationFrame, not setInterval: under real main-thread load
    // (MediaPipe + WS traffic + 4 charts all competing), a delayed
    // setInterval callback can fire a backlog in a sudden burst once the
    // thread frees up — stuck, then jumps. rAF never queues up missed
    // ticks, and pacing off real elapsed time (not a fixed "1 sample per
    // tick") keeps this exactly DRAIN_HZ regardless of the display's actual
    // refresh rate (60Hz, 120Hz, or throttled under load).
    let rafId = 0;
    let lastDrainAt = performance.now();
    let carry = 0;
    const tick = (now: number) => {
      const elapsedMs = now - lastDrainAt;
      lastDrainAt = now;
      carry += (elapsedMs / 1000) * DRAIN_HZ;
      const toDrain = Math.min(Math.floor(carry), queueRef.current.length);
      carry -= Math.floor(carry);
      if (toDrain > 0 && chartRef.current) {
        const buf = chartRef.current.data.datasets[0].data as number[];
        for (let i = 0; i < toDrain; i++) {
          buf.shift();
          buf.push(queueRef.current.shift()!);
        }
        if (hasGroundTruth && chartRef.current.data.datasets[1]) {
          const gtBuf = chartRef.current.data.datasets[1].data as number[];
          const gtDrain = Math.min(toDrain, gtQueueRef.current.length);
          for (let i = 0; i < gtDrain; i++) {
            gtBuf.shift();
            gtBuf.push(gtQueueRef.current.shift()!);
          }
        }
        chartRef.current.update('none');
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isMonitoring, hasGroundTruth]);

  return (
    <Line
      ref={chartRef}
      data={chartData}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        // Unlike the other three waveforms, BP's absolute scale is clinically
        // meaningful (mmHg), not just a shape to glance at — shown per
        // reviewer feedback.
        scales: {
          x: { display: false },
          y: {
            display: true,
            ticks: { color: 'rgba(255,255,255,0.35)', font: { size: 9 }, maxTicksLimit: 4 },
            grid: { color: 'rgba(255,255,255,0.06)' },
            border: { display: false },
          },
        },
      }}
    />
  );
};

export default React.memo(BPWaveChart);
